import { Encoding } from '@mode-vegas/vegas';
import { OrderedSet } from 'immutable';

import { ResultColumns } from '../flamingo.types';
import { FieldFormatMap } from './field-format.types';
import { ChartFormat, FieldColorMap, FacetSizeMap } from './viz-format.types';
import { ImmutableMap, RMap, StateRecord, ReadonlyCollection } from '../common.types';
import { ModeResource } from '../mode-api';
import { UserResource } from '../../lib/account/user.types';

export type V1ChartType =
  | 'bar'
  | 'area'
  | 'bigNumber'
  | 'line'
  | 'linePlusBar'
  | 'stackedBar'
  | 'pie'
  | 'pivotTable'
  | 'scatter';

export type ChartType =
  | 'bar'
  | 'hBar'
  | 'line'
  | 'stackedBar'
  | 'hStackedBar'
  | 'stackedBar100'
  | 'hStackedBar100'
  | 'pie'
  | 'donut'
  | 'linePlusBar'
  | 'area'
  | 'totalArea'
  | 'bigValue'
  | 'pivotTable'
  | 'scatter'
  | 'vegas'
  | 'table'
  | 'vegasPivotTable'
  | 'histogram'
  | 'spreadsheet';

export const VegasChartTypes = ['vegas', 'vegasPivotTable'];

export function isVegasChartType(chartType: ChartType) {
  return VegasChartTypes.find((vegasChartType) => vegasChartType === chartType) != null;
}

export const MuzeRenderedChartTypes = [
  'bar',
  'hBar',
  'line',
  'stackedBar',
  'hStackedBar',
  'stackedBar100',
  'hStackedBar100',
  'pie',
  'donut',
  'linePlusBar',
  'area',
  'totalArea',
  'scatter',
  'vegas',
  'vegasPivotTable',
];

export function canMuzeRenderChart(chartType: ChartType) {
  return MuzeRenderedChartTypes.includes(chartType);
}

// Defines the object type which differentiates between a `visualization` and
// an `exploration` in the designer and other contexts.
export const enum ObjectType {
  Visualization = 'visualization',
  Exploration = 'exploration',
}

export interface ChartView {
  selectedChart: V1ChartType;
  area: any;
  bar: any;
  bigNumber: any;
  line: any;
  linePlusBar: any;
  pie: any;
  pivotTable: any;
  scatter: any;
  chartTitle: string;
  chartDescription: string;
  chartIcon: string;
  _vizReportRunLink?: string;
  _vizLink?: string;
}

export const enum ChartCaptionPosition {
  Top = 'top',
  Bottom = 'bottom',
}

export interface CaptionInfo {
  text: string;
  isHidden: boolean;
  isTitleHidden: boolean;
  position: ChartCaptionPosition;
}

export const EMPTY_CAPTION: CaptionInfo = {
  text: '',
  isHidden: false,
  isTitleHidden: false,
  position: ChartCaptionPosition.Top,
};

export interface ViewVegas {
  title: string;
  description: string;
  caption: CaptionInfo;
  chartType: ChartType;
  encoding: Encoding;
  format: Readonly<ChartFormat>;
  fieldFormats: FieldFormatMap;
  fieldColors: Readonly<FieldColorMap>;
  facetSizes: Readonly<FacetSizeMap>;
  isAutoUpdateDisabled: boolean;
  _vizReportRunLink?: string;
  _vizLink?: string;
}

// This representations the format of the exploration used to persist
// via the api
export interface ExplorationChart {
  view_vegas: ViewVegas;
  view_version: 2;
  color_palette_token: string;
}

export interface ExplorationResource extends StateRecord, ModeResource {
  name: string;
  created_at: string;
  updated_at: string;
  token: string;
  query_token: string;
  query_name: string;
  report_name: string;

  _links: {
    [key: string]: { href: string };
  };

  _embedded: {
    chart: ChartResource;
    creator: UserResource;
  };
}

export interface ChartResource extends StateRecord, ModeResource {
  view_vegas: ViewVegas;
  view: ChartView;
  view_version: 1 | 2;
  color_palette_token: string;
  switch_view_token: string;
  _parent?: string;
  created_at: string;
}

export interface VegasRenderInfo extends ViewVegas {
  reportLevelColorMap: { [seriesName: string]: number };
}

export interface RawData {
  content: ReadonlyArray<any>;
  columns: ResultColumns; // Can also be a legacy query result schema, (any[])
}

export type ChartSnapshot = Pick<
  ViewVegas,
  | 'encoding'
  | 'format'
  | 'fieldFormats'
  | 'fieldColors'
  | 'facetSizes'
  | 'chartType'
  | 'title'
  | 'description'
  | 'isAutoUpdateDisabled'
  | 'caption'
> & {
  colorPaletteToken: string;
};

export interface ChartHistoryEntry {
  action: string;
  state: ChartSnapshot;
}

export interface ChartHistory {
  undoStack: ReadonlyCollection<ChartHistoryEntry>;
  last: ChartHistoryEntry['state'];
  redoStack: ReadonlyCollection<ChartHistoryEntry>;
}

export interface ChartPaging {
  offset: number;
  pageSize: number;
}

export interface TableColumnSearch {
  [key: string]: any;
}

export interface ChartFeatureState {
  selectedToken: string | null;

  tokens: OrderedSet<string>;
  versions: ImmutableMap<1 | 2>;
  queryTokens: ImmutableMap<string>;
  switchViewTokens: ImmutableMap<string>;
  resources: ImmutableMap<ChartResource>;
  createdTimestamps: ImmutableMap<string>;

  titles: ImmutableMap<string | undefined>;
  descriptions: ImmutableMap<string | undefined>;

  captions: ImmutableMap<CaptionInfo>;
  chartTypes: ImmutableMap<ChartType>;
  encodings: ImmutableMap<Encoding>;
  formats: ImmutableMap<ChartFormat>;
  fieldFormats: ImmutableMap<FieldFormatMap>;
  fieldColors: ImmutableMap<FieldColorMap>;
  colorPaletteTokens: ImmutableMap<string>;
  facetSizes: ImmutableMap<FacetSizeMap>;
  isAutoUpdateDisabled: ImmutableMap<boolean>;
  manuallyApplyChanges: ImmutableMap<boolean>;
  unappliedChangesCount: ImmutableMap<number>;
  ephemerals: ImmutableMap<boolean>;

  v1Views: ImmutableMap<ChartView>;
  histories: ImmutableMap<ChartHistory>;

  tablePaging: RMap<ChartPaging>;
  tableColumnSearch: RMap<TableColumnSearch>;

  suppressSpreadsheetRefresh: ImmutableMap<boolean>;

  selectedFieldToFormat: string | null;
}

export interface CoreChart extends StateRecord {
  resource: ChartResource;
  queryToken: string;
  switchViewToken: string;
  version: 1 | 2;

  // Available in case legacy chart components need to use the new state. Ideally they will be deprecated
  // and won't ever need to be migrated.
  v1View: any;

  history: ChartHistory;
  format: Readonly<ChartFormat>;
  fieldFormats: Readonly<FieldFormatMap>;
  fieldColors: Readonly<FieldColorMap>;
  facetSizes: Readonly<FacetSizeMap>;
  chartType: ChartType;
  title: string;
  description: string;
  caption: CaptionInfo;

  colorPaletteToken: string;
  isAutoUpdateDisabled: boolean;
}

export interface EncodedChart extends CoreChart {
  encoding: Readonly<Encoding>; // EncodingResult is technically derived state
}

export const enum PillActionType {
  RemovePill = 'RemovePill',
  ChangePillFormula = 'ChangePillFormula',
}

/**
 * Helper type for editor-browser and query-nav metadata types.
 */
export interface ChartTabMetadata {
  chartToken: string;
  title: string;
  type: ChartType;
  legacyType: V1ChartType | undefined;
  viewVersion: 1 | 2;
  createdAt: string;
}

/**
 * GF Response Types
 */
export type VisualizationResponse = ChartResource;
